import { useState, useEffect, useMemo, useCallback } from "react";
import apiClient from "../utils/apiClient";

/**
 * useAiModels — loads the AI model catalogue for an admin authoring screen and
 * holds the admin's pick.
 *
 * WHY A HOOK AND NOT A CONSTANT
 * ─────────────────────────────
 * The list is NOT hardcoded here, and must not be. Which ids exist is a server
 * fact (an enterprise install talks to its own gateway and has none of the
 * OpenAI catalogue), and the id the admin picks goes straight into a paid API
 * call — so the server is the one that decides what is offerable and validates
 * what comes back. The FE's job is to show the list and send the choice.
 *
 * Every `…/ai-models`-shaped endpoint returns the same payload
 * (`vc_be/utils/aiModelCatalog.js` → `aiModelsPayload`):
 *
 *   { models: [{ id, label, note, reasoning, speed }],
 *     pickable, defaultModel, efforts, defaultEffort, enabled, quota? }
 *
 * so one hook reads all of them. Endpoints today:
 *
 *   /cms/ai/models             CMS pages, blogs, course marketing, syllabus, SEO
 *   /quiz-questions/ai-models  quiz question bank + subtopic folders
 *   /questions/ai-models       coding question bank
 *   /ai/ai-questions/ai-models interview question bank
 *
 * `/marketing/ai/models` is deliberately NOT one of these — that list is asked
 * of the provider live and carries per-model quota weights, because ad copy is
 * billed against a different meter. Use its own panel, not this hook.
 *
 * WHAT TO SEND BACK
 * ─────────────────
 * Spread `payload` into the generate request. It carries `model` and, only when
 * the chosen model actually thinks, `reasoningEffort` — sending an effort to a
 * non-reasoning model is a 400 from the provider.
 *
 * @param {string}  url            the endpoint above for this surface
 * @param {boolean} active         false while the modal is closed, so a screen
 *                                 that merely CONTAINS a generate modal does not
 *                                 fetch a catalogue nobody asked for
 */
export default function useAiModels(url, active = true) {
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [model, setModel] = useState("");
  const [reasoningEffort, setReasoningEffort] = useState("");

  useEffect(() => {
    if (!active || !url) return undefined;
    let alive = true;
    setLoading(true);
    apiClient
      .get(url)
      .then(({ data }) => {
        if (!alive) return;
        setMeta(data);
        // The server's recommendation wins until the admin overrides it, so an
        // admin who never opens the dropdown still gets the best model rather
        // than whatever happens to sort first.
        setModel((prev) => prev || data?.defaultModel || data?.models?.[0]?.id || "");
      })
      .catch((err) => {
        if (!alive) return;
        // A catalogue that will not load must never block the generation — the
        // server applies its own default when `model` is absent, which is
        // exactly the behaviour this whole screen had before the picker existed.
        setError(err?.response?.data?.message || "Could not load the AI model list.");
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [url, active]);

  const models = useMemo(() => meta?.models || [], [meta]);
  const activeModel = useMemo(() => models.find((m) => m.id === model), [models, model]);

  /* Two separate reasons to hide the dropdown, and they are not the same thing:
     the edition has no curated ids to offer (`pickable: false`), or there is
     only one row — and a select with a single option is furniture, not a
     choice. */
  const pickable = meta?.pickable !== false && models.length > 1;

  /* The thinking control is meaningless on a model that does not think, and a
     dead dropdown reads as a broken one — so it only exists when it applies. */
  const showEffort = !!activeModel?.reasoning;

  const payload = useMemo(() => ({
    model,
    ...(showEffort && reasoningEffort ? { reasoningEffort } : {}),
  }), [model, showEffort, reasoningEffort]);

  const reset = useCallback(() => {
    setModel(meta?.defaultModel || meta?.models?.[0]?.id || "");
    setReasoningEffort("");
  }, [meta]);

  return {
    // For <AiModelPicker {...ai} />
    models,
    model,
    setModel,
    reasoningEffort,
    setReasoningEffort,
    pickable,
    showEffort,
    activeModel,
    // For the generate call
    payload,
    // Everything else the surface may want to show
    meta,
    quota: meta?.quota || null,
    enabled: meta?.enabled !== false,
    loading,
    error,
    reset,
  };
}
